import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components_dashboard/ui/button';
import { Input } from '../components_dashboard/ui/input';
import { Label } from '../components_dashboard/ui/label';
import { Mail, Lock, Eye, EyeOff, Loader2, Home, ArrowLeft, ShieldCheck, KeyRound } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const navigate = useNavigate();

  // Steps: 'email' → 'otp' → 'reset'
  const [step, setStep] = useState('email');

  // Email step
  const [email, setEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  // OTP step
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [resetToken, setResetToken] = useState('');
  const otpRefs = useRef([]);

  // Reset step
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');

  // Validation
  const hasLowerUpper = /(?=.*[a-z])(?=.*[A-Z])/.test(newPassword);
  const hasNumberOrSymbol = /(?=.*[0-9!@#$%^&*()_+{}:"<>?|~`\-=[\]\\;',.\/])/.test(newPassword);
  const isMinLength = newPassword.length >= 6;
  const noSpacesOrPipe = newPassword.length > 0 && !/[\s|]/.test(newPassword);
  const isMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const canReset = hasLowerUpper && hasNumberOrSymbol && isMinLength && noSpacesOrPipe && isMatch && !resetLoading;

  // Focus first OTP input when entering OTP step
  useEffect(() => {
    if (step === 'otp' && otpRefs.current[0]) {
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    }
  }, [step]);

  // ── Step 1: Send OTP ──
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setEmailError('');
    setEmailLoading(true);
    try {
      await axios.post(`${API_URL}/api/v1/auth/forgot-password`, { email });
      setEmailSent(true);
      setStep('otp');
    } catch (err) {
      setEmailError(err.response?.data?.detail || 'Failed to send OTP. Please try again.');
    } finally {
      setEmailLoading(false);
    }
  };

  // ── Step 2: Verify OTP ──
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    setOtpError('');

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(''));
      otpRefs.current[5]?.focus();
    }
  };

  const handleVerifyOtp = async (e) => {
    if (e) e.preventDefault();
    const otpStr = otp.join('');
    if (otpStr.length !== 6) {
      setOtpError('Please enter all 6 digits');
      return;
    }
    setOtpError('');
    setOtpLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/v1/auth/verify-reset-otp`, {
        email,
        otp: otpStr,
      });
      setResetToken(res.data.reset_token);
      setStep('reset');
    } catch (err) {
      setOtpError(err.response?.data?.detail || 'Invalid OTP. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // Auto-submit when all 6 digits are entered
  useEffect(() => {
    if (otp.every((d) => d !== '') && step === 'otp') {
      handleVerifyOtp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  // ── Step 3: Reset Password ──
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!canReset) return;
    setResetError('');
    setResetLoading(true);
    try {
      await axios.post(`${API_URL}/api/v1/auth/reset-password`, {
        reset_token: resetToken,
        new_password: newPassword,
      });
      navigate('/login', { state: { passwordReset: true } });
    } catch (err) {
      setResetError(err.response?.data?.detail || 'Failed to reset password.');
    } finally {
      setResetLoading(false);
    }
  };

  // ── Step indicator ──
  const steps = [
    { key: 'email', label: 'Email', icon: Mail },
    { key: 'otp', label: 'Verify', icon: ShieldCheck },
    { key: 'reset', label: 'Reset', icon: KeyRound },
  ];
  const currentIdx = steps.findIndex((s) => s.key === step);

  return (
    <div className="flex items-center justify-center min-h-screen p-4 login-bg">
      <div className="w-full max-w-md">
        <div className="relative p-8 glass-modal rounded-2xl shadow-float animate-slide-up">
          {/* Back button */}
          <Link
            to="/login"
            className="absolute flex items-center justify-center transition-colors rounded-full top-4 left-4 w-9 h-9 bg-blue-50 hover:bg-blue-100 group"
            title="Back to login"
          >
            <ArrowLeft className="w-5 h-5 text-blue-600 group-hover:text-blue-800" strokeWidth={2.5} />
          </Link>

          {/* Header */}
          <div className="mb-6 text-center">
            <div className="inline-flex items-center gap-2 mb-2">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-accent">
                <span className="text-lg font-bold text-white">T</span>
              </div>
              <span className="text-2xl font-bold tracking-tight text-slate-900">TechLadder.ai</span>
            </div>
            <p className="text-sm text-slate-500">Reset your password</p>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {steps.map((s, i) => {
              const Icon = s.icon;
              const isActive = i === currentIdx;
              const isDone = i < currentIdx;
              return (
                <div key={s.key} className="flex items-center gap-2">
                  {i > 0 && (
                    <div
                      className={`w-8 h-0.5 rounded transition-colors duration-300 ${
                        isDone ? 'bg-emerald-400' : 'bg-slate-200'
                      }`}
                    />
                  )}
                  <div
                    className={`flex items-center justify-center w-8 h-8 rounded-full transition-all duration-300 ${
                      isActive
                        ? 'bg-primary text-white shadow-md scale-110'
                        : isDone
                        ? 'bg-emerald-100 text-emerald-600'
                        : 'bg-slate-100 text-slate-400'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Step 1: Email ── */}
          {step === 'email' && (
            <form onSubmit={handleSendOtp} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="forgot-email" className="font-medium text-slate-700">
                  Registered Email
                </Label>
                <div className="relative">
                  <Mail className="absolute w-4 h-4 -translate-y-1/2 left-3 top-1/2 text-slate-400" />
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                    className="pl-10 h-11 border-slate-200 focus:border-accent focus:ring-accent"
                    required
                    autoFocus
                  />
                </div>
                <p className="text-xs text-slate-400">
                  We'll send a 6-digit verification code to this email.
                </p>
              </div>

              {emailError && (
                <div className="p-3 text-sm text-red-600 border border-red-100 rounded-lg bg-red-50">
                  {emailError}
                </div>
              )}

              <Button
                type="submit"
                className="w-full font-medium text-white rounded-lg h-11 bg-primary hover:bg-primary-700 shadow-soft"
                disabled={emailLoading || !email}
              >
                {emailLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending OTP...
                  </>
                ) : (
                  'Send Verification Code'
                )}
              </Button>
            </form>
          )}

          {/* ── Step 2: OTP ── */}
          {step === 'otp' && (
            <form onSubmit={handleVerifyOtp} className="space-y-5">
              <div className="text-center">
                <p className="text-sm text-slate-600">
                  Enter the 6-digit code sent to
                </p>
                <p className="mt-1 font-semibold text-slate-800">{email}</p>
              </div>

              <div className="flex justify-center gap-2">
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => (otpRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    onPaste={i === 0 ? handleOtpPaste : undefined}
                    className={`w-11 h-13 text-center text-xl font-bold rounded-lg border-2 outline-none transition-all duration-200
                      ${digit ? 'border-primary bg-primary/5 text-primary' : 'border-slate-200 bg-white text-slate-800'}
                      focus:border-primary focus:ring-2 focus:ring-primary/20`}
                    style={{ height: '52px' }}
                  />
                ))}
              </div>

              {otpError && (
                <div className="p-3 text-sm text-center text-red-600 border border-red-100 rounded-lg bg-red-50">
                  {otpError}
                </div>
              )}

              <Button
                type="submit"
                className="w-full font-medium text-white rounded-lg h-11 bg-primary hover:bg-primary-700 shadow-soft"
                disabled={otpLoading || otp.some((d) => !d)}
              >
                {otpLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify Code'
                )}
              </Button>

              <button
                type="button"
                onClick={() => { setStep('email'); setOtp(['', '', '', '', '', '']); setOtpError(''); }}
                className="w-full text-sm font-medium text-center transition-colors text-slate-500 hover:text-primary"
              >
                Didn't receive the code? Try again
              </button>
            </form>
          )}

          {/* ── Step 3: Reset Password ── */}
          {step === 'reset' && (
            <form onSubmit={handleResetPassword} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="new-password" className="font-medium text-slate-700">New Password</Label>
                <div className="relative">
                  <Lock className="absolute w-4 h-4 -translate-y-1/2 left-3 top-1/2 text-slate-400" />
                  <Input
                    id="new-password"
                    type={showNew ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); setResetError(''); }}
                    className="pl-10 pr-10 h-11 border-slate-200 focus:border-accent focus:ring-accent"
                    required
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(!showNew)}
                    className="absolute -translate-y-1/2 right-3 top-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="font-medium text-slate-700">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute w-4 h-4 -translate-y-1/2 left-3 top-1/2 text-slate-400" />
                  <Input
                    id="confirm-password"
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setResetError(''); }}
                    className="pl-10 pr-10 h-11 border-slate-200 focus:border-accent focus:ring-accent"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute -translate-y-1/2 right-3 top-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Password rules */}
              <div className="pl-1 space-y-1 text-xs">
                <div className={`flex items-center gap-1.5 transition-colors ${newPassword.length > 0 && hasLowerUpper ? 'text-emerald-600' : 'text-slate-400'}`}>
                  — Include lower and upper characters
                </div>
                <div className={`flex items-center gap-1.5 transition-colors ${newPassword.length > 0 && hasNumberOrSymbol ? 'text-emerald-600' : 'text-slate-400'}`}>
                  — Include at least 1 number or symbol
                </div>
                <div className={`flex items-center gap-1.5 transition-colors ${newPassword.length > 0 && isMinLength ? 'text-emerald-600' : 'text-slate-400'}`}>
                  — Be at least 6 characters long
                </div>
                <div className={`flex items-center gap-1.5 transition-colors ${confirmPassword.length > 0 && isMatch ? 'text-emerald-600' : 'text-slate-400'}`}>
                  — Passwords match
                </div>
                <div className={`flex items-center gap-1.5 transition-colors ${newPassword.length > 0 && noSpacesOrPipe ? 'text-emerald-600' : 'text-slate-400'}`}>
                  — No spaces or "|" symbol
                </div>
              </div>

              {resetError && (
                <div className="p-3 text-sm text-red-600 border border-red-100 rounded-lg bg-red-50">
                  {resetError}
                </div>
              )}

              <Button
                type="submit"
                className="w-full font-medium text-white rounded-lg h-11 bg-primary hover:bg-primary-700 shadow-soft"
                disabled={!canReset}
              >
                {resetLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
